import { FC, useMemo } from 'react';
import { useController } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Box, Dropdown, DropdownInput, DropdownMenu, DropdownOption, Flex, RefreshOutline, Text } from '@bonusly/design-system';
import { assertPresence } from '@bonusly/runtime';

export const FrequencyInput: FC = () => {
	const { t } = useTranslation('one_on_ones');
	const { field } = useController({ name: 'frequency' });

	// NOTE: In the previous iteration all of the options were generated in a single hook called useCadenceOptions.
	// https://gitlab.com/bonusly/engineering/front-end/kaleidoscope/-/blob/e9b53010a0e2dd2a9ccf8f030790a6b278f0aa97/packages/common/meeting-modal/src/components/meeting/hooks/use-cadence-options.ts
	// This was kind of unnecessary because the options are only used in this one place. I refactored it so that each of the
	// inputs generated the options themselves.
	const options = useMemo(() => {
		return [
			{
				label: t('meeting.modal.form.cadence.recurrence_options.weekly'),
				value: 'weekly',
			},
			{
				label: t('meeting.modal.form.cadence.recurrence_options.biweekly'),
				value: 'biweekly',
			},
			{
				label: t('meeting.modal.form.cadence.recurrence_options.monthly'),
				value: 'monthly',
			},
		];
	}, [t]);

	return (
		<Box flex="1 1 50%">
			<Dropdown
				onChange={field.onChange}
				renderOption={(value) => {
					const selected = options.find((o) => o.value === value);

					// NOTE: We're asserting here because we know that there is always going to be a value, even though the `.find` method *technically* could return undefined.
					// However, we've limited the available options and the selected value, so if it's ever `undefined` then we did something wrong and this assertion will
					// throw an error, which we want to happen. Below this line we know that `selected` exists and don't need to use safe navigation checks like `selected?.label`,
					// which makes for more declarative and conscise code.
					assertPresence(selected);

					return (
						<Flex justifyContent="left" w="full" gap={2}>
							<RefreshOutline />
							<Text color="text.default">{selected.label}</Text>
						</Flex>
					);
				}}
				value={field.value}
			>
				<DropdownInput data-testid="frequency-input" minW={0} width="full" />
				<DropdownMenu zIndex="popover">
					{options.map((option) => (
						<DropdownOption key={option.value} value={option.value}>
							{option.label}
						</DropdownOption>
					))}
				</DropdownMenu>
			</Dropdown>
		</Box>
	);
};
